import SequenceNumberHandler from './SequenceNumberHandler';
import TrackingIdEnum from '../enums/TrackingIdEnum';
import IdGenerator from './IdGenerator';
import ITrackingProps from '../types/ITrackingProps';
import IEventData from '../types/IEventData';
import IUserIds from '../types/IUserIds';
import MetadataManager from './MetadataManager';

const localStorageKeys = [TrackingIdEnum.longTermIdKey, TrackingIdEnum.ipexIdKey];
const sessionStorageKeys = [TrackingIdEnum.sessionIdKey, TrackingIdEnum.planningIdKey];

/**
 * Utility class for handling data about the session.
 */
export default class SessionManager {
	private sequenceNumber = new SequenceNumberHandler();
	private idGenerator = new IdGenerator();

	/**
	 * Get the current session Id if one exists, or creates one and returns it if it doesn't.
	 *
	 * @param key The request session type.
	 * @returns The valid tracking Id.
	 */
	public getValidId(key: TrackingIdEnum): ITrackingProps {
		const trackingId = this.getId(key);
		if (trackingId) {
			return trackingId;
		}

		return this.createId(key);
	}

	/**
	 * Update the time for when the session started to now, and saves it to storage.
	 * Will throw an Error if no session exists.
	 *
	 * @param key The key for the id to update.
	 * @returns The new session id object which has been saved.
	 */
	public updateIdTime(key: TrackingIdEnum): ITrackingProps {
		const pastId = this.getId(key);

		if (!pastId) {
			throw Error('No session to update');
		}

		const newIdProps = {
			time: Date.now(),
			id: pastId.id
		};

		if (localStorageKeys.includes(key)) {
			localStorage.setItem(key, JSON.stringify(newIdProps));
		} else if (sessionStorageKeys.includes(key)) {
			this.safeSessionSet(key, newIdProps);
		}
		return newIdProps;
	}

	/**
	 * Ensure the ids in local and session storage exist and are valid. Will create and/or update values if necessary.
	 */
	public updateIds(): IEventData | undefined {
		const sessionId = this.getId(TrackingIdEnum.sessionIdKey);
		let longTermId = this.getId(TrackingIdEnum.longTermIdKey);
		let ipexId = this.getId(TrackingIdEnum.ipexIdKey);

		if (!longTermId) {
			longTermId = this.createId(TrackingIdEnum.longTermIdKey);
		}
		if (!ipexId || Date.now() - ipexId.time > 30 * 60 * 1000) {
			ipexId = this.createId(TrackingIdEnum.ipexIdKey);
		} else {
			this.updateIdTime(TrackingIdEnum.ipexIdKey);
		}

		if (!sessionId) {
			this.createId(TrackingIdEnum.sessionIdKey);
			this.sequenceNumber.resetSequenceNumber();
			return {
				['long_term_id_created']: longTermId.time,
				['ipex_id_created']: ipexId.time
			};
		} else if (Date.now() - sessionId.time > 30 * 60 * 1000) {
			// No interaction the past 30 minutes. Create a new session, but leave the long term id as is.
			this.createId(TrackingIdEnum.sessionIdKey);
			this.sequenceNumber.resetSequenceNumber();
			return {
				['long_term_id_created']: longTermId.time,
				['ipex_id_created']: ipexId.time
			};
		} else {
			this.updateIdTime(TrackingIdEnum.sessionIdKey);
			return undefined;
		}
	}

	/**
	 * Clear all session related data.
	 */
	public clearSessions(): void {
		localStorage.clear();
		sessionStorage.clear();
	}

	/**
	 * Create a new id for given key, generates an uuidV4 as id and sets time to now.
	 *
	 * @param key The key to generate a value for.
	 * @param startSource Optional planning start source, which will be saved if the key is for planning ids.
	 * @param sourceDesignId Optional design id, which will be saved if the key is for planning ids.
	 * @param sourceSpr Optional spr source, which will be saved if the key is for planning ids.
	 * @returns The new value object.
	 */
	public createId(
		key: TrackingIdEnum,
		startSource?: string,
		sourceDesignId?: string,
		sourceSpr?: string
	): ITrackingProps {
		const id = this.idGenerator.uuidV4();
		const newIdProps: ITrackingProps = {
			time: Date.now(),
			id
		};
		if (key === TrackingIdEnum.planningIdKey && startSource) {
			newIdProps.startSource = startSource;
			newIdProps.sourceDesignId = sourceDesignId;
			newIdProps.sourceSpr = sourceSpr;
		}
		if (localStorageKeys.includes(key)) {
			localStorage.setItem(key, JSON.stringify(newIdProps));
		} else if (sessionStorageKeys.includes(key)) {
			this.safeSessionSet(key, newIdProps);
		}
		return newIdProps;
	}

	/**
	 * Get the value for supplied key.
	 *
	 * @param key The key which to look for.
	 * @returns The value for the key. Will be null if no value exists.
	 */
	public getId(key: TrackingIdEnum): ITrackingProps {
		if (localStorageKeys.includes(key)) {
			const idProps = localStorage.getItem(key);
			return idProps ? JSON.parse(idProps) : null;
		} else if (sessionStorageKeys.includes(key)) {
			return this.safeSessionGet(key);
		}
		return null;
	}

	/**
	 * Get user ids.
	 *
	 * @returns The user ids (sessionId, planningId, longTermId and ipexId).
	 */
	public getIds(): IUserIds {
		return {
			sessionId: this.getId(TrackingIdEnum.sessionIdKey)?.id,
			planningId: this.getId(TrackingIdEnum.planningIdKey)?.id,
			longTermId: this.getId(TrackingIdEnum.longTermIdKey)?.id,
			ipexId: this.getId(TrackingIdEnum.ipexIdKey)?.id
		};
	}

	/**
	 * Save some props to a planner-prepended key, but only if planner name is registered in metadata.
	 *
	 * @param key The session storage key to save to.
	 * @param props The props to save to the key.
	 */
	private safeSessionSet(key: TrackingIdEnum, props: ITrackingProps): void {
		const metadata = MetadataManager.getApplicationMetadata();
		if ('applicationId' in metadata) {
			const applicationIdKey = metadata.applicationId.concat('_', key);
			sessionStorage.setItem(applicationIdKey, JSON.stringify(props));
		}
	}

	/**
	 * Retrieve some props from a planner-prepended key, but only if planner name is registered in metadata.
	 *
	 * @param key The session storage key to save to.
	 * @returns The ITrackingProps saved to the key if it exists, otherwise null.
	 */
	private safeSessionGet(key: TrackingIdEnum): ITrackingProps {
		const metadata = MetadataManager.getApplicationMetadata();
		let idProps;
		if (metadata?.applicationId) {
			const applicationIdKey = metadata.applicationId.concat('_', key);
			idProps = sessionStorage.getItem(applicationIdKey);
		}
		return idProps ? JSON.parse(idProps) : null;
	}
}
